import LineChart from "src/common/charts/lineChart";
import moment from "moment-timezone";
import { DataPoint, StockData } from "src/helpers";
import { useCallback, useMemo } from "react";
import { X } from "lucide-react";

interface StockDataProps {
  isOpen: boolean;
  onClose: () => void;
  stock: DataPoint[];
  darkMode: boolean | undefined;
  selectedStock: StockData | string | null;
  selectedDate?: Date | null;
  isDashboardModal?: boolean;
  isLoadingChart?: boolean;
  onExpand?: () => void;
}

interface PeakReturnData {
  peakIndex: number;
  peakValue: number;
  returnPercentage: number;
  daysToPeak: number;
}

const ChartModal = (props: StockDataProps) => {
  const {
    isOpen,
    onClose,
    stock,
    darkMode,
    selectedStock,
    selectedDate,
    isDashboardModal,
    isLoadingChart,
    onExpand,
  } = props;

  // Hooks must be called unconditionally (before any early returns)
  const formatDateToIST = useCallback(
    (date: Date | string) =>
      moment.utc(date).tz("Asia/Kolkata").format("YYYY-MM-DD"),
    [],
  );

  // Calculate complete peak return data to pass to LineChart
  const peakReturnData = useMemo((): PeakReturnData | null => {
    if (!isDashboardModal || !selectedDate || !stock?.length) return null;

    const dates = stock.map((item) => formatDateToIST(item.date));
    const closes = stock.map((item) => item.close);

    const selectedDateStr = formatDateToIST(selectedDate);
    const selectedIndex = dates.indexOf(selectedDateStr);

    if (selectedIndex < 0 || selectedIndex >= stock.length - 1) {
      return null;
    }

    const entryPrice = closes[selectedIndex];
    const dataAfterSelected = stock.slice(selectedIndex + 1);

    // Find the highest HIGH after selected date
    const peakHigh = Math.max(...dataAfterSelected.map((item) => item.high));

    // Only proceed if peak is higher than entry
    if (peakHigh <= entryPrice) {
      return null;
    }

    // Find which date has this peak high
    const peakIndex = stock.findIndex(
      (item, idx) => idx > selectedIndex && item.high === peakHigh,
    );

    if (peakIndex === -1) return null;

    // Calculate return percentage and days to peak
    const returnPercentage = ((peakHigh - entryPrice) / entryPrice) * 100;

    const peakDate = stock[peakIndex]?.date;
    const peakDateStr = peakDate ? formatDateToIST(peakDate) : null;
    const daysToPeak =
      peakDateStr !== null
        ? Math.max(0, moment(peakDateStr).diff(moment(selectedDateStr), "days"))
        : 0;

    return {
      peakIndex,
      peakValue: peakHigh,
      returnPercentage,
      daysToPeak,
    };
  }, [isDashboardModal, selectedDate, stock, formatDateToIST]);

  if (!isOpen) return null;

  const symbol =
    typeof selectedStock === "string"
      ? selectedStock
      : selectedStock?.symbol || "";

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center backdrop-blur-md p-4 h-full"
      onClick={onClose}
    >
      <div
        className={`w-full rounded-2xl overflow-hidden shadow-2xl z-10 bg-white dark:bg-gray-900/90 ${onExpand ? "max-w-7xl" : "max-w-5xl"} `}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-1 py-1 flex justify-between items-center">
          <h2 className="text-lg dark:text-white font-semibold mb-1 ml-3">
            {symbol}
            {isDashboardModal ? (
              peakReturnData ? (
                <span className="ml-2 text-sm font-semibold text-emerald-600 dark:text-emerald-400">
                  {`${peakReturnData.returnPercentage.toFixed(
                    2,
                  )}% achieved in ${peakReturnData.daysToPeak} days`}
                </span>
              ) : null
            ) : (
              ` - 1 Year Chart (TF: 1 Day)`
            )}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-white p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="rounded-lg h-96">
          {isLoadingChart ? (
            <div className="flex items-center justify-center h-full bg-white dark:bg-[#121419]">
              <p className="text-gray-600 dark:text-gray-300 text-center text-base font-medium">
                Loading chart data...
              </p>
            </div>
          ) : stock?.length > 0 ? (
            <LineChart
              key={`${symbol}-${selectedDate}-${peakReturnData?.peakValue}`}
              selectedDate={selectedDate}
              symbolName={symbol}
              lineData={stock}
              darkMode={!!darkMode}
              isAnalyzeStock={true}
              isExpand={true}
              isDashboardModal={isDashboardModal}
            />
          ) : (
            <div className="flex items-center justify-center h-full bg-white dark:bg-[#121419]">
              <p className="text-gray-600 dark:text-gray-300 text-center text-base font-medium">
                No data available
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default ChartModal;
